import React from 'react';

/**
 * @typedef {Object} IntersectionObserverOptions
 * @property {Element} root - ancestor element.
 * @property {string} rootMargin - root margin.
 * @property {Number|Number[]} threshold - threshold.
 */

/**
 * @typedef {Object} UseIntersectionResults
 * @property {React.MutableRefObject<T>} sentinelRef - target element ref.
 * @property {Boolean} isIntersecting - is element intersecting.
 */

/**
 * React hook to observe changes in the intersection of a target element
 * with an ancestor element or with a top-level document's viewport.
 * @param {IntersectionObserverOptions} options - options to the IntersectionObserver constructor.
 * @returns {UseIntersectionResults} - results object.
 */

const useIntersection = (options = {}) => {
  const sentinelRef = React.useRef(null);

  const [isIntersecting, setIsIntersecting] = React.useState(false);

  React.useEffect(() => {
    const observedElement = sentinelRef.current;
    const callback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
        } else {
          setIsIntersecting(false);
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      observer.unobserve(observedElement);
    };
  }, [options]);

  return { sentinelRef, isIntersecting };
};

export default useIntersection;
